/**
 * THE FIELD MAP'S PURE HALF — projection and freshness, no DOM. v22-D.
 *
 * ===========================================================================
 * WHY THERE IS NO MAP LIBRARY AND NO BASEMAP (the brief's reported decision)
 * ===========================================================================
 * The library chosen is NONE, for reasons that are rule 14's, not taste's:
 *
 *  1. THIRD-PARTY TILES ARE A POSITION-BEARING CHANNEL. A raster/vector
 *     basemap means fetching tiles for the viewport — and a tile address at
 *     street zoom (z/x/y) IS the neighbourhood being looked at. Rendering the
 *     team's positions over OSM/Google tiles broadcasts "someone at this
 *     company is looking at THIS block" to a third party and every network
 *     middlebox, request by request. Rule 14 applies to every channel
 *     including the map's own payload; tile URLs are such a channel.
 *  2. SELF-HOSTED TILES ARE INFRASTRUCTURE THIS PASS MAY NOT CREATE. A
 *     privacy-clean basemap (MapLibre + self-hosted PMTiles) needs a tile
 *     asset served from owner infrastructure — a deployment decision that is
 *     the owner's, named in the report as the street-level upgrade path.
 *  3. THE OPERATIONAL QUESTION — where is each person, relative to each
 *     other, how fresh — is answered at team scale by a projected plane with
 *     a real scale bar. Street context, when a holder needs it, is one
 *     deliberate click per person (the open-in-OSM link on the roster), which
 *     disloses ONE chosen coordinate by explicit act — the same act as
 *     reading the number off the register and typing it into a browser.
 *
 * ===========================================================================
 * THE PROJECTION — honest at team scale, and it says so
 * ===========================================================================
 * Equirectangular about the marker centroid's latitude: x = lng·cos(φ₀),
 * y = −lat. Over a team's spread (a city, a district) the distortion is
 * negligible; the scale bar is computed from the same cos(φ₀), so distances
 * READ off the map match the maths that drew it. This is not a chart for
 * navigation — it is "who is where, right now", with the geometry honest at
 * the zoom it serves.
 */

export interface ProjectedPoint {
  x: number;
  y: number;
}

export interface MapView {
  /** SVG viewBox, "x y w h" in projected degrees. */
  viewBox: string;
  /** Projected width/height in degrees — for aspect handling. */
  width: number;
  height: number;
  /** Kilometres per projected-degree unit at the centroid latitude. */
  kmPerUnit: number;
  /** A round-number scale bar that spans roughly a quarter of the view. */
  scaleBarKm: number;
  scaleBarUnits: number;
  project: (lat: number, lng: number) => ProjectedPoint;
}

const KM_PER_DEGREE = 111.32;

/** Round a raw length down to a 1/2/5×10ⁿ ladder value — map-scale-bar convention. */
export function roundScale(rawKm: number): number {
  if (rawKm <= 0) return 1;
  const power = Math.floor(Math.log10(rawKm));
  const base = 10 ** power;
  const mantissa = rawKm / base;
  const step = mantissa >= 5 ? 5 : mantissa >= 2 ? 2 : 1;
  return step * base;
}

/**
 * Fit the view around the markers.
 *
 * `minSpanDegrees` guards the degenerate cases: ONE person (a point has no
 * bbox) and a team standing in one office. 0.02° ≈ 2.2 km — a single marker
 * gets a neighbourhood-scale frame rather than an infinite zoom onto a dot.
 */
export function fitView(
  points: readonly { lat: number; lng: number }[],
  aspect = 4 / 3,
  minSpanDegrees = 0.02,
): MapView | null {
  if (points.length === 0) return null;

  const lat0 =
    points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const cos0 = Math.max(Math.cos((lat0 * Math.PI) / 180), 0.01);
  const project = (lat: number, lng: number): ProjectedPoint => ({
    x: lng * cos0,
    y: -lat,
  });

  const projected = points.map((p) => project(p.lat, p.lng));
  let minX = Math.min(...projected.map((p) => p.x));
  let maxX = Math.max(...projected.map((p) => p.x));
  let minY = Math.min(...projected.map((p) => p.y));
  let maxY = Math.max(...projected.map((p) => p.y));

  // Pad by 15% each side, then enforce the minimum span in both axes.
  const padX = Math.max((maxX - minX) * 0.15, (minSpanDegrees * cos0) / 2);
  const padY = Math.max((maxY - minY) * 0.15, minSpanDegrees / 2);
  minX -= padX;
  maxX += padX;
  minY -= padY;
  maxY += padY;

  // Stretch the short axis to the requested aspect so the SVG never letterboxes.
  let width = maxX - minX;
  let height = maxY - minY;
  if (width / height < aspect) {
    const grow = height * aspect - width;
    minX -= grow / 2;
    width = height * aspect;
  } else {
    const grow = width / aspect - height;
    minY -= grow / 2;
    height = width / aspect;
  }

  // One projected unit on x is one degree of longitude·cos(φ₀) — which is
  // KM_PER_DEGREE km on the ground. y units are latitude degrees, same km.
  const kmPerUnit = KM_PER_DEGREE;
  const scaleBarKm = roundScale((width * kmPerUnit) / 4);
  return {
    viewBox: `${String(minX)} ${String(minY)} ${String(width)} ${String(height)}`,
    width,
    height,
    kmPerUnit,
    scaleBarKm,
    scaleBarUnits: scaleBarKm / kmPerUnit,
    project,
  };
}

/** The three freshness classes the markers wear — live is a STATE, not an age. */
export type Freshness = 'live' | 'recent' | 'stale';

export function freshnessOf(
  live: boolean,
  capturedAtIso: string,
  serverTimeIso: string,
): Freshness {
  if (live) return 'live';
  const age = Date.parse(serverTimeIso) - Date.parse(capturedAtIso);
  // 30 minutes: within the near-live dashboard's cadence a stamp still
  // describes "now-ish"; beyond it the marker is HISTORY and dresses as such.
  return age <= 30 * 60 * 1000 ? 'recent' : 'stale';
}

/**
 * The age, said the way a person would — always relative to the SERVER's
 * clock. "A stamp from this morning is not a live position and the map must
 * not imply it is": the words are the guarantee, so they are computed here
 * and tested, not improvised per render.
 */
export function ageSentence(capturedAtIso: string, serverTimeIso: string): string {
  const ms = Date.parse(serverTimeIso) - Date.parse(capturedAtIso);
  if (Number.isNaN(ms)) return 'age unknown';
  if (ms < 60 * 1000) return 'just now';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} h ${String(minutes % 60)} min ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${String(days)} days ago`;
}

/** What the stamp's source verb says to a reader. */
export const SOURCE_LABEL: Record<string, string> = {
  live_session: 'live session',
  clock_in: 'clocked in',
  clock_out: 'clocked out',
  visit_arrive: 'arrived at a visit',
  visit_leave: 'left a visit',
};
